import React, { useState, useCallback, memo } from 'react';
import { connect } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { DEFAULT_AUDIO_TRACK, MEDIA_RESOURCE_TYPE } from '../CreatePageConstants';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { isSafariBrowser } from '../../../../utilities/browserUtilities';

const styles = theme => ({
    warningsWrapper: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: theme.spacing(2)
    },
    itemWrapper: {
        textAlign: 'start'
    },
    icon: {
        color: theme.palette.warning.main,
        position: 'relative',
        top: theme.spacing(0.6),
        marginRight: theme.spacing(0.5),
        cursor: 'pointer',
        display: 'inline'
    },
    text: {
        color: theme.palette.warning.main,
        textShadow: '0px 0px 5px #000000',
        cursor: 'pointer',
        display: 'inline',
        lineHeight: 1
    }
});

const safariVideoWarningText = `
    Editing with video backgrounds enabled can be unstable in Safari. 
    Google Chrome is recommended for video editing. 
    Videos exported on Safari will still export without issue.
`;

const audioAnalysisWarningText = `We are unable to sync the preview with your audio. Exported videos will still sync successfully with your audio.`;

const WarningItem = memo(({ classes, message, collapsed, onClick }) => (
    <Box className={classes.itemWrapper}>
        <WarningRoundedIcon 
            className={classes.icon} 
            onClick={onClick}
        />
        {!collapsed && (
            <Typography 
                variant='body1' 
                className={classes.text} 
                onClick={onClick}
            >
                {message}
            </Typography>
        )}
    </Box>
));

const PreviewDisplayWarnings = (props) => {
    const { 
        classes, 
        backgroundMediaResourceType, 
        audioAnalyzing, 
        bassMaxMagnitude, 
        wideMaxMagnitude, 
        audioUrl, 
        audioLoading, 
        audioPreparing 
    } = props;
    
    // State hooks to replace class state
    const [audioAnalysisWarningCollapsed, setAudioAnalysisWarningCollapsed] = useState(true);
    const [safariVideoWarningCollapsed, setSafariVideoWarningCollapsed] = useState(false);

    // Callback functions for handling clicks
    const onAudioAnalysisWarningClick = useCallback(() => {
        setAudioAnalysisWarningCollapsed(prev => !prev);
    }, []);

    const onSafariVideoWarningClick = useCallback(() => {
        setSafariVideoWarningCollapsed(prev => !prev);
    }, []);

    // Early return if we're in a loading state
    if (audioAnalyzing || audioLoading || audioPreparing) {
        return null;
    }

    // Check for Safari video warning
    const isSafariVideoWarningPresent = (backgroundMediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO && isSafariBrowser());
    if (isSafariVideoWarningPresent) {
        return (
            <Box className={classes.warningsWrapper}> 
                <WarningItem
                    classes={classes}
                    message={safariVideoWarningText}
                    collapsed={safariVideoWarningCollapsed}
                    onClick={onSafariVideoWarningClick}
                />
            </Box>
        );
    }

    // Check for audio analysis warning.
    // Show the warning when the analyzed audio magnitudes are empty and the audio is not the default one.
    // The magnitudes are empty when the audio analysis is failed or interrupted.
    const isAudioMagnitudesEmpty = (!bassMaxMagnitude || !wideMaxMagnitude);
    const isDefaultAudio = audioUrl && audioUrl === DEFAULT_AUDIO_TRACK.SOUND_URL;
    if (isAudioMagnitudesEmpty && !isDefaultAudio) {
        return (
            <Box className={classes.warningsWrapper}> 
                <WarningItem
                    classes={classes}
                    message={audioAnalysisWarningText}
                    collapsed={audioAnalysisWarningCollapsed}
                    onClick={onAudioAnalysisWarningClick}
                />
            </Box>
        );
    }

    return null;
}

const mapStateToProps = (state) => {
    const { audioDuration, audioAnalyzing, audioLoading, audioPreparing } = state.create;
    const { mediaResourceType: backgroundMediaResourceType } = state.createPreview.present.videoSettings.background.mediaSource;
    const { bassMaxMagnitude, wideMaxMagnitude, url: audioUrl } = state.create.audioSettings;
    
    return { 
        audioDuration, 
        audioAnalyzing,
        backgroundMediaResourceType,
        bassMaxMagnitude,
        wideMaxMagnitude,
        audioUrl,
        audioLoading,
        audioPreparing
    };
};

export default connect(mapStateToProps)(withStyles(PreviewDisplayWarnings, styles));